import { z } from "zod";

export const offeredServiceStatusSchema = z.enum(["ACTIVE", "INACTIVE"]);
export type OfferedServiceStatus = z.infer<typeof offeredServiceStatusSchema>;

const optionalEmpty = (max = 200) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .or(z.literal("").transform(() => undefined));

/** Cor em hex válido (#RRGGBB) — mesma validação de agenda-settings.ts. */
const hexColor = z
  .string()
  .regex(/^#[0-9A-Fa-f]{6}$/, "Cor deve ser hex no formato #RRGGBB");

const offeredServiceBaseSchema = z.object({
  name: z.string().trim().min(2, "Nome muito curto").max(160),
  /** Especialidade principal (agrupa nas telas). Sem ela, a primeira de `especialidadeIds`. */
  especialidadeId: z.string().min(1).optional(),
  /** Especialidades em que o procedimento serve (OfferedServiceSpecialty). */
  especialidadeIds: z.array(z.string().min(1)).max(40).optional(),
  // Opcional porque só OWNER define preço (ver assertCanSetPrice em routers/service.ts) — quem
  // gerencia o catálogo sem ser dono cadastra o item e o dono precifica depois.
  priceCents: z.number().int().min(0, "Preço inválido").default(0),
  durationMinutes: z.number().int().min(1).max(1000).optional(),
  /** Cor do card na agenda quando esse serviço é escolhido. Null = card usa a cor do status
   *  (ver resolveCardColor em apps/app/src/lib/agenda-colors.ts). */
  color: hexColor.nullable().optional(),
  /** Cobrado por dente: no plano de tratamento, o preço multiplica pela quantidade de dentes. */
  cobradoPorDente: z.boolean().optional(),
  /** Unidade da quantidade no modo Face do plano (U, ml, frasco...). */
  unidade: z.string().trim().max(20).nullish().transform((v) => v || null),
  description: optionalEmpty(2000),
  status: offeredServiceStatusSchema.default("ACTIVE"),
});

// Sem especialidade nenhuma (ex.: criar rápido na agenda), o servidor usa Clínico Geral.
export const offeredServiceCreateSchema = offeredServiceBaseSchema;
export const offeredServiceUpdateSchema = offeredServiceBaseSchema.partial().extend({
  id: z.string().min(1),
  // Sem o default: no zod 4 o default vale mesmo com o campo ausente, e editar sem mandar o campo o reescreveria.
  priceCents: offeredServiceBaseSchema.shape.priceCents.unwrap().optional(),
  status: offeredServiceStatusSchema.optional(),
});

export type OfferedServiceCreateInput = z.infer<typeof offeredServiceCreateSchema>;
export type OfferedServiceUpdateInput = z.infer<typeof offeredServiceUpdateSchema>;

export const offeredServiceListFiltersSchema = z.object({
  search: z.string().trim().max(200).optional(),
  especialidadeId: z.string().min(1).optional(),
  status: offeredServiceStatusSchema.optional(),
  take: z.number().int().min(1).max(500).default(100),
  skip: z.number().int().min(0).default(0),
});
export type OfferedServiceListFilters = z.infer<typeof offeredServiceListFiltersSchema>;

/** Importar do Catálogo (laboratório) — cria vários de uma vez. Preço é escolhido na hora da
 *  importação (em grupo ou individual, decidido no formulário) — aqui só valida o que chega. */
export const serviceImportManySchema = z.object({
  items: z
    .array(
      z.object({
        name: z.string().trim().min(2).max(160),
        especialidadeId: z.string().min(1).optional(),
        priceCents: z.number().int().min(0).default(0),
        description: optionalEmpty(2000),
      }),
    )
    .min(1)
    .max(100),
});
export type ServiceImportManyInput = z.infer<typeof serviceImportManySchema>;

export const offeredServiceIdsInputSchema = z.object({
  ids: z.array(z.string().min(1)).min(1),
});
export type OfferedServiceIdsInput = z.infer<typeof offeredServiceIdsInputSchema>;

/**
 * A especialidade principal do procedimento: a escolhida, se for uma das dele; senão a primeira
 * delas; sem nenhuma, a reserva (Clínico Geral). A principal sempre entra nas especialidades.
 */
export function especialidadePrincipal(escolhida: string | undefined, especialidadeIds: string[], reserva: string) {
  const principal = escolhida && (especialidadeIds.length === 0 || especialidadeIds.includes(escolhida)) ? escolhida : (especialidadeIds[0] ?? reserva);
  return { principal, especialidadeIds: especialidadeIds.includes(principal) ? especialidadeIds : [principal, ...especialidadeIds] };
}
